<template>
  <v-dialog v-model="dialog" max-width="520" scrollable persistent>
    <v-card v-if="flow">
      <v-card-title class="subtitle-1 font-weight-bold">
        {{ flow.rules.name }}
      </v-card-title>
      <v-card-subtitle class="text-wrap pb-2">{{ hint }}</v-card-subtitle>

      <v-card-text class="px-2">
        <v-list dense>
          <v-list-item
            v-for="target in flow.targets"
            :key="target.id"
            class="promo-target"
            @click="book(target)"
          >
            <v-list-item-avatar v-if="target.image" tile size="56">
              <v-img :src="target.image" :alt="target.name" />
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold">
                {{ target.name }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ target.sellPrice | currency }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-icon color="accent">mdi-plus-circle</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text color="error" @click="dismiss">
          {{ $t("delivery.lang_cancel") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import promoCode from "@/mixins/promoCode";
import {
  PROMO_CONDITIONS,
  evaluatePromoConditions
} from "@/utils/promoCode";

export default {
  name: "PromoTargetDialog",

  mixins: [promoCode],

  props: {
    value: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      handedOver: false
    };
  },

  computed: {
    ...createNamespacedHelpers("ItemGroups").mapGetters(["itemGroups"]),
    ...createNamespacedHelpers("Order").mapGetters(["promoTargetRequest"]),

    flow() {
      return this.value;
    },

    // hidden while the menu's booking dialog has the stage
    dialog: {
      get() {
        return !!this.flow && !this.handedOver;
      },
      set(val) {
        if (!val) this.dismiss();
      }
    },

    condition() {
      return this.flow
        ? evaluatePromoConditions(this.cart, this.flow.rules)
        : null;
    },

    hint() {
      const result = this.condition;
      if (!result || result.ok) return "";
      if (result.reason === PROMO_CONDITIONS.NOT_ENOUGH_ITEMS)
        return this.$t("delivery.lang_promoPickMoreItems", {
          count: result.missingQuantity
        });
      return this.$t("delivery.lang_promoPickAnItem");
    }
  },

  watch: {
    value(flow) {
      this.handedOver = false;
      // a single target needs no picking, open it straight away
      if (flow && flow.targets.length === 1) this.book(flow.targets[0]);
    },

    // menu closed its booking dialog without a booking, take the stage back
    promoTargetRequest(request) {
      if (!request && this.flow) this.handedOver = false;
    },

    // any cart change can complete the promo, whichever way the item got booked
    cart: {
      deep: true,
      handler() {
        if (!this.flow || !this.condition || !this.condition.ok) return;

        this.$store.commit("Order/addPromoItem", this.flow.rules);
        this.promoSuccess(this.$t("delivery.lang_promoApplied"));
        this.reset();
      }
    }
  },

  methods: {
    /** Hands the target to the menu, its booking dialog owns sizes and garnish. */
    book(target) {
      const itemGroup =
        this.itemGroups.find(
          group => String(group.id) === String(target.groupId)
        ) || target.itemGroup || null;

      this.handedOver = true;
      this.$store.commit("Order/setPromoTargetRequest", {
        item: target,
        itemGroup
      });

      if (this.$route.name !== "RestaurantMenu")
        this.$router.push({ name: "RestaurantMenu" });
    },

    // customer walked away from the promo: keep what they booked, explain the drop
    dismiss() {
      const result = this.condition;
      if (result && !result.ok) {
        this.promoInfo(
          result.reason === PROMO_CONDITIONS.NOT_ENOUGH_ITEMS
            ? this.$t("delivery.lang_promoNotAppliedMissing", {
                count: result.missingQuantity
              })
            : this.$t("delivery.lang_promoNotApplied")
        );
      }
      this.reset();
    },

    reset() {
      this.handedOver = false;
      this.$store.commit("Order/setPromoTargetRequest", null);
      this.$emit("input", null);
    }
  }
};
</script>

<style scoped>
.promo-target {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  margin-bottom: 8px;
}
</style>
